/**
 * Brouillon d'édition d'un aliment — état de formulaire, sans React.
 *
 * Même partage des rôles que `templateDraft.ts` : un module qui exporte des
 * composants ne doit rien exporter d'autre (`react-refresh`), et ces
 * conversions se relisent mieux séparées du rendu.
 *
 * ⚠️ `per100` décrit les macros **pour 100 g ou 100 ml** — jamais celles d'une
 * entrée de journal. Le total d'une entrée reste calculé par le serveur (§5.1),
 * à partir de ces valeurs et de la quantité consommée.
 */

import type { BaseUnit, FoodDTO, FoodInput, OffProductDTO } from '../../../../shared/types';
import { newClientId } from '../../lib/dates';
import {
  EMPTY_MACROS,
  fieldsOf,
  macrosOf,
  toField,
  toNumber,
  type MacroFields,
} from '../../lib/formFields';

export interface PortionDraft {
  /** Clé de rendu locale — jamais envoyée au serveur, jamais persistée. */
  key: string;
  label: string;
  /** Valeur en unité de base, en chaîne le temps de la saisie. */
  quantity: string;
}

export interface FoodDraft {
  name: string;
  brand: string;
  barcode: string;
  baseUnit: BaseUnit;
  per100: MacroFields;
  isFavorite: boolean;
  portions: PortionDraft[];
  /**
   * Code Open Food Facts d'origine, quand la fiche est créée par import.
   * ⚠️ Jamais modifiable ensuite : c'est une provenance, pas un état.
   */
  offCode: string | null;
}

export function emptyFoodDraft(barcode = ''): FoodDraft {
  return {
    name: '',
    brand: '',
    barcode,
    baseUnit: 'g',
    per100: { ...EMPTY_MACROS },
    isFavorite: false,
    portions: [],
    offCode: null,
  };
}

export function draftOfFood(food: FoodDTO): FoodDraft {
  return {
    name: food.name,
    brand: food.brand ?? '',
    barcode: food.barcode ?? '',
    baseUnit: food.baseUnit,
    per100: fieldsOf(food.per100),
    isFavorite: food.isFavorite,
    portions: food.portions.map((portion) => ({
      key: newClientId(),
      label: portion.label,
      quantity: toField(portion.quantity),
    })),
    offCode: food.offCode,
  };
}

/**
 * Brouillon pré-rempli depuis un produit Open Food Facts (specs §6.4).
 *
 * ⚠️ Les macros arrivent **éditables**, et c'est une exigence : « les données
 * Open Food Facts sont traitées comme peu fiables par défaut ; les macros
 * doivent être visibles et éditables avant validation de l'ajout ». Ce
 * pré-remplissage est une proposition, pas un import silencieux.
 *
 * La portion annoncée par OFF est reprise quand elle existe : c'est presque
 * toujours la portion utile (« 30 g », « 1 pot »), et la resaisir à la main à
 * chaque produit serait exactement le genre de friction que le §6.4 cherche à
 * éviter.
 */
export function draftOfOffProduct(product: OffProductDTO): FoodDraft {
  return {
    name: product.name,
    brand: product.brand ?? '',
    barcode: product.code,
    baseUnit: product.baseUnit,
    per100: fieldsOf(product.per100),
    isFavorite: false,
    portions:
      product.servingQuantity === null
        ? []
        : [
            {
              key: newClientId(),
              label: product.servingLabel ?? '1 portion',
              quantity: toField(product.servingQuantity),
            },
          ],
    offCode: product.code,
  };
}

/** Portions retenues : celles dont le libellé et la quantité tiennent debout. */
function portionsOf(draft: FoodDraft) {
  return draft.portions
    .filter((portion) => portion.label.trim() !== '' && toNumber(portion.quantity) > 0)
    .map((portion) => ({ label: portion.label.trim(), quantity: toNumber(portion.quantity) }));
}

/** Le formulaire est-il envoyable ? Un aliment sans nom n'a pas d'identité. */
export function isFoodDraftValid(draft: FoodDraft): boolean {
  return draft.name.trim() !== '';
}

export function inputOfDraft(draft: FoodDraft, clientId?: string): FoodInput {
  const barcode = draft.barcode.trim();
  return {
    name: draft.name.trim(),
    brand: draft.brand.trim() === '' ? null : draft.brand.trim(),
    barcode: barcode === '' ? null : barcode,
    baseUnit: draft.baseUnit,
    per100: macrosOf(draft.per100),
    isFavorite: draft.isFavorite,
    portions: portionsOf(draft),
    offCode: draft.offCode,
    ...(clientId === undefined ? {} : { clientId }),
  };
}

/** Corps de `PATCH` — `offCode` et la provenance ne sont jamais modifiés. */
export function updateOfDraft(draft: FoodDraft) {
  const barcode = draft.barcode.trim();
  return {
    name: draft.name.trim(),
    brand: draft.brand.trim() === '' ? null : draft.brand.trim(),
    barcode: barcode === '' ? null : barcode,
    baseUnit: draft.baseUnit,
    per100: macrosOf(draft.per100),
    isFavorite: draft.isFavorite,
    portions: portionsOf(draft),
  };
}
